import { supabase } from "@/integrations/supabase/client";
import { fragorData } from "@/data/fragorData";
import { specialRiskFragor } from "@/lib/fragorData/specialrisker";
import { AMP_SPECIAL_RISKS } from "./constants";
import type { AmpSourceKind } from "./constants";

const questionText = (id: string) => fragorData.find((f) => f.id === id)?.text || id;
const questionCategory = (id: string) => fragorData.find((f) => f.id === id)?.kategori || "Övrigt";

const SPECIAL_IDS = new Set(specialRiskFragor.map((f) => f.id));

export interface AmpSourceResult {
  text: string;
  /** Antal poster i underlaget (risker, kontrollpunkter, möten ...) */
  count: number;
  /** Senaste ändring i källdokumentet, används för att visa "underlaget har uppdaterats" */
  sourceUpdatedAt: string | null;
  /** Strukturerade data att lägga in i avsnittets formulär */
  structuredData?: Record<string, any>;
}

const empty: AmpSourceResult = { text: "", count: 0, sourceUpdatedAt: null };

const latest = (rows: Array<{ updated_at?: string | null; created_at?: string | null }> | null | undefined) => {
  const stamps = (rows || [])
    .map((r) => r.updated_at || r.created_at)
    .filter(Boolean) as string[];
  if (!stamps.length) return null;
  return stamps.sort().slice(-1)[0];
};

/** Hämtar underlag + metadata från befintliga dokument i projektet till ett AMP-avsnitt. */
export async function fetchAmpSourceDetailed(
  source: AmpSourceKind,
  projectId: string
): Promise<AmpSourceResult> {
  if (source === "project" || source === "organisation") {
    const { data } = await supabase
      .from("projects")
      .select(
        "name, description, omfattning, syfte, bakgrund, ansvar, upprattat_av, handlaggande_basp, ort, datum, namnfortydligande, created_at"
      )
      .eq("id", projectId)
      .maybeSingle();
    if (!data) return empty;

    const rows: string[] = [];
    if (source === "project") {
      if (data.name) rows.push(`Projekt: ${data.name}`);
      if (data.ort) rows.push(`Ort: ${data.ort}`);
      if (data.datum) rows.push(`Datum: ${data.datum}`);
      if (data.description) rows.push(`Beskrivning: ${data.description}`);
      if (data.omfattning) rows.push(`Omfattning: ${data.omfattning}`);
      if (data.syfte) rows.push(`Syfte: ${data.syfte}`);
      if (data.bakgrund) rows.push(`Bakgrund: ${data.bakgrund}`);
      const structuredData: Record<string, any> = {
        projectName: data.name ?? "",
        city: data.ort ?? "",
        description: [data.description, data.omfattning, data.syfte, data.bakgrund]
          .filter(Boolean)
          .join("\n\n"),
      };
      return { text: rows.join("\n"), count: rows.length, sourceUpdatedAt: data.created_at ?? null, structuredData };
    } else {
      if (data.ansvar) rows.push(`Ansvar: ${data.ansvar}`);
      if (data.handlaggande_basp) rows.push(`Handläggande Bas-P: ${data.handlaggande_basp}`);
      if (data.upprattat_av) rows.push(`Upprättat av: ${data.upprattat_av}`);
      if (data.namnfortydligande) rows.push(`Namnförtydligande: ${data.namnfortydligande}`);
      const structuredData: Record<string, any> = {
        client: { company: data.ansvar ?? "", representative: data.namnfortydligande ?? "" },
        basP: { name: data.handlaggande_basp ?? "" },
      };
      return { text: rows.join("\n"), count: rows.length, sourceUpdatedAt: data.created_at ?? null, structuredData };
    }
  }

  if (source === "forhandsanmalan") {
    const { data } = await supabase
      .from("process_steps")
      .select("status, note, updated_at")
      .eq("project_id", projectId)
      .eq("doc_number", "04")
      .maybeSingle();
    if (!data) return empty;
    const rows: string[] = [];
    rows.push(`Status i Bas-processen: ${data.status}`);
    if (data.note) rows.push(data.note);
    return { text: rows.join("\n"), count: 1, sourceUpdatedAt: data.updated_at ?? null };
  }

  if (source === "risk" || source === "sarskilda") {
    const { data } = await supabase
      .from("folder_risk_assessments")
      .select("name, data, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .eq("phase", "byggskede");
    if (!data?.length) return empty;

    const lines: string[] = [];
    let count = 0;
    const structuredRisks: Record<string, { applicable: boolean; measures?: string }> = {};
    for (const assessment of data) {
      const svar = ((assessment.data as any)?.svar || {}) as Record<string, any>;
      const hits = Object.entries(svar).filter(([id, v]) => {
        if (String(v?.svar || "").toLowerCase() !== "ja") return false;
        return source === "sarskilda" ? SPECIAL_IDS.has(id) : !SPECIAL_IDS.has(id);
      });
      if (!hits.length) continue;
      lines.push(`Från ${assessment.name}:`);
      for (const [id, v] of hits) {
        count += 1;
        const parts = [`• [${questionCategory(id)}] ${questionText(id)}`];
        if (v?.kommentar) parts.push(`  Risk: ${v.kommentar}`);
        if (v?.atgard) parts.push(`  Åtgärd: ${v.atgard}`);
        lines.push(parts.join("\n"));
        if (source === "sarskilda") {
          structuredRisks[id] = { applicable: true, measures: v?.atgard || v?.kommentar || "" };
        }
      }
      lines.push("");
    }
    const structuredData: Record<string, any> | undefined =
      source === "sarskilda"
        ? {
            risks: AMP_SPECIAL_RISKS.map((r) => ({
              ...r,
              applicable: structuredRisks[r.id]?.applicable ?? null,
              measures: structuredRisks[r.id]?.measures ?? "",
            })),
          }
        : undefined;
    return { text: lines.join("\n").trim(), count, sourceUpdatedAt: latest(data), structuredData };
  }

  if (source === "skr") {
    const { data: project } = await supabase
      .from("projects")
      .select("skr_analysis")
      .eq("id", projectId)
      .maybeSingle();

    const lines: string[] = [];
    let count = 0;
    const skr = project?.skr_analysis as any;
    if (skr) {
      if (skr.size || skr.storlek) lines.push(`Storlek: ${skr.size ?? skr.storlek}`);
      if (skr.complexity || skr.komplexitet) lines.push(`Komplexitet: ${skr.complexity ?? skr.komplexitet}`);
      if (skr.risk || skr.riskniva) lines.push(`Riskprofil: ${skr.risk ?? skr.riskniva}`);
      if (skr.summary || skr.sammanfattning) lines.push(String(skr.summary ?? skr.sammanfattning));
      count += 1;
    }

    const { data: assessments } = await supabase
      .from("skr_assessments")
      .select("name, omfattning, syfte, status, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null);
    for (const a of assessments || []) {
      count += 1;
      lines.push(`Från ${a.name}${a.status ? ` (${a.status})` : ""}:`);
      if (a.omfattning) lines.push(`  Omfattning: ${a.omfattning}`);
      if (a.syfte) lines.push(`  Syfte: ${a.syfte}`);
    }
    return { text: lines.join("\n").trim(), count, sourceUpdatedAt: latest(assessments) };
  }

  if (source === "workarea") {
    const { data: surveys } = await supabase
      .from("work_area_surveys")
      .select("id, area_name, delimitation, adjacent_activity, notes, updated_at, created_at")
      .eq("project_id", projectId);
    if (!surveys?.length) return empty;

    const ids = surveys.map((s) => s.id);
    const { data: items } = await supabase
      .from("work_area_survey_items")
      .select("survey_id, category, control_point, status, comment, responsible, updated_at")
      .in("survey_id", ids)
      .eq("status", "risk");

    const lines: string[] = [];
    for (const s of surveys) {
      lines.push(`Arbetsområde: ${s.area_name}`);
      if (s.delimitation) lines.push(`  Avgränsning: ${s.delimitation}`);
      if (s.adjacent_activity) lines.push(`  Angränsande verksamhet: ${s.adjacent_activity}`);
      if (s.notes) lines.push(`  Noteringar: ${s.notes}`);
      const own = (items || []).filter((i) => i.survey_id === s.id);
      for (const i of own) {
        lines.push(`  • [${i.category}] ${i.control_point}`);
        if (i.comment) lines.push(`    Hantering: ${i.comment}`);
        if (i.responsible) lines.push(`    Ansvarig: ${i.responsible}`);
      }
      lines.push("");
    }
    return {
      text: lines.join("\n").trim(),
      count: (items || []).length,
      sourceUpdatedAt: latest([...(surveys as any[]), ...((items as any[]) || [])]),
    };
  }

  if (source === "ordningsregler" || source === "safety_rules") {
    const { data: docs } = await supabase
      .from("safety_rule_documents")
      .select("id, name, intro, project_specific, updated_at, created_at")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (!docs?.length) return empty;

    const { data: rules } = await supabase
      .from("safety_rule_items")
      .select("document_id, category, text, legal_reference, included, sort_order, updated_at, created_at")
      .in("document_id", docs.map((d) => d.id))
      .eq("included", true)
      .order("sort_order", { ascending: true });

    const lines: string[] = [];
    for (const d of docs) {
      lines.push(`${d.name}${d.project_specific ? " (projektspecifika)" : " (generiska)"}`);
      if (d.intro) lines.push(`  ${d.intro}`);
      const own = (rules || []).filter((r) => r.document_id === d.id);
      let lastCat = "";
      for (const r of own) {
        if (r.category !== lastCat) {
          lines.push(`  ${r.category}:`);
          lastCat = r.category;
        }
        lines.push(`  • ${r.text}${r.legal_reference ? ` (${r.legal_reference})` : ""}`);
      }
      lines.push("");
    }
    return {
      text: lines.join("\n").trim(),
      count: (rules || []).length,
      sourceUpdatedAt: latest([...(docs as any[]), ...((rules as any[]) || [])]),
    };
  }

  if (source === "skyddsanordningar" || source === "followup") {

    const { data } = await supabase
      .from("execution_followup_items")
      .select("area, title, description, status, responsible, comment, updated_at, created_at")
      .eq("project_id", projectId)
      .order("sort_order", { ascending: true });
    if (!data?.length) return empty;

    const lines: string[] = [];
    for (const i of data) {
      lines.push(`• ${i.area ? `[${i.area}] ` : ""}${i.title} (${i.status})`);
      if (i.description) lines.push(`  ${i.description}`);
      if (i.responsible) lines.push(`  Ansvarig: ${i.responsible}`);
      if (i.comment) lines.push(`  Kommentar: ${i.comment}`);
    }
    return { text: lines.join("\n").trim(), count: data.length, sourceUpdatedAt: latest(data) };
  }

  if (source === "samordning" || source === "meetings") {
    const { data } = await supabase
      .from("coordination_meetings")
      .select("meeting_date, participants, status, content_json, updated_at, created_at")
      .eq("project_id", projectId)
      .order("meeting_date", { ascending: true });
    if (!data?.length) return empty;

    const lines: string[] = [];
    for (const m of data) {
      lines.push(`• Samordningsmöte ${m.meeting_date} (${m.status})`);
      if (m.participants) lines.push(`  Deltagare: ${m.participants}`);
      const content = (m.content_json || {}) as Record<string, any>;
      Object.entries(content).forEach(([k, v]) => {
        if (typeof v === "string" && v.trim()) lines.push(`  ${k}: ${v.trim()}`);
      });
    }
    return { text: lines.join("\n").trim(), count: data.length, sourceUpdatedAt: latest(data) };
  }

  if (source === "bruksskede") {
    const { data: risks } = await supabase
      .from("residual_use_risks")
      .select("space_name, risk_description, handling, discipline, legal_reference, status, updated_at, created_at")
      .eq("project_id", projectId);
    const { data: checklists } = await supabase
      .from("bruksskede_checklists")
      .select("space_name, description, status, updated_at, created_at")
      .eq("project_id", projectId);

    const { data: bruksAssessments } = await supabase
      .from("folder_risk_assessments")
      .select("name, data, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .eq("phase", "bruksskede");

    const lines: string[] = [];
    for (const a of bruksAssessments || []) {
      const svar = ((a.data as any)?.svar || {}) as Record<string, any>;
      const hits = Object.entries(svar).filter(
        ([, v]) => String((v as any)?.svar || "").toLowerCase() === "ja"
      );
      if (!hits.length) continue;
      lines.push(`Från risksammanställning (bruksskede) ${a.name}:`);
      for (const [id, v] of hits) {
        lines.push(`• [${questionCategory(id)}] ${questionText(id)}`);
        if ((v as any)?.kommentar) lines.push(`  Risk: ${(v as any).kommentar}`);
        if ((v as any)?.atgard) lines.push(`  Åtgärd: ${(v as any).atgard}`);
      }
      lines.push("");
    }
    for (const c of checklists || []) {
      lines.push(`Utrymme: ${c.space_name}${c.status ? ` (${c.status})` : ""}`);
      if (c.description) lines.push(`  ${c.description}`);
    }
    if ((risks || []).length) {
      lines.push("");
      lines.push("Kvarvarande bruksrisker:");
      for (const r of risks || []) {
        lines.push(`• ${r.space_name ? `[${r.space_name}] ` : ""}${r.risk_description}`);
        if (r.handling) lines.push(`  Hantering: ${r.handling}`);
        if (r.discipline) lines.push(`  Disciplin: ${r.discipline}`);
        if (r.legal_reference) lines.push(`  Referens: ${r.legal_reference}`);
      }
    }
  if (!lines.length) return empty;
  return {
    text: lines.join("\n").trim(),
    count: (risks || []).length,
    sourceUpdatedAt: latest([...((checklists as any[]) || []), ...((risks as any[]) || [])]),
  };
}

  if (source === "work_preparations") {
    const { data } = await supabase
      .from("work_preparations")
      .select("name, status, data, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("created_at", { ascending: true });
    if (!data?.length) return empty;

    const lines: string[] = [];
    for (const wp of data) {
      lines.push(`• ${wp.name}${wp.status ? ` (${wp.status})` : ""}`);
      const d = (wp.data || {}) as Record<string, any>;
      if (d.fields?.beskrivArbetet) lines.push(`  Beskrivning: ${d.fields.beskrivArbetet}`);
      if (d.fields?.kontaktinformation) lines.push(`  Kontakt: ${d.fields.kontaktinformation}`);
    }
    return { text: lines.join("\n").trim(), count: data.length, sourceUpdatedAt: latest(data) };
  }

  if (source === "bilagor") {
    // AMP är alltid ett dokument för hela projektet. Bilagorna kan däremot
    // höra till en viss etapp eller ett visst område – därför märks varje
    // bilaga med sin etapp/område i förteckningen.
    const appendices: {
      name: string;
      doc: string;
      included: boolean;
      note: string;
      scope: string;
    }[] = [];
    const add = (name: string, doc: string, scope = "", note = "") =>
      appendices.push({ name, doc, included: false, note, scope });

    const { data: stages } = await supabase
      .from("project_stages")
      .select("id, name")
      .eq("project_id", projectId);
    const stageName = (id?: string | null) =>
      (stages || []).find((s) => s.id === id)?.name || "";

    const { data: surveys } = await supabase
      .from("work_area_surveys")
      .select("id, area_name, stage_id, updated_at, created_at")
      .eq("project_id", projectId);
    const areaName = (id?: string | null) =>
      (surveys || []).find((s) => s.id === id)?.area_name || "";
    const scopeOf = (stageId?: string | null, areaSurveyId?: string | null) =>
      [stageName(stageId), areaName(areaSurveyId)].filter(Boolean).join(" · ");

    const { data: riskAssessments } = await supabase
      .from("folder_risk_assessments")
      .select("name, phase, stage_id, area_survey_id, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null);
    for (const r of riskAssessments || []) {
      const doc = r.phase === "bruksskede" ? "31" : "30b";
      add(r.name, doc, scopeOf(r.stage_id, r.area_survey_id));
    }

    const { data: skrAssessments } = await supabase
      .from("skr_assessments")
      .select("name, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null);
    for (const s of skrAssessments || []) add(s.name, "03");

    for (const s of surveys || []) add(s.area_name, "10", stageName(s.stage_id));

    const { data: safetyDocs } = await supabase
      .from("safety_rule_documents")
      .select("name, updated_at, created_at")
      .eq("project_id", projectId);
    for (const s of safetyDocs || []) add(s.name, "15");

    const { data: workPreps } = await supabase
      .from("work_preparations")
      .select("name, stage_id, area_survey_id, updated_at, created_at")
      .eq("project_id", projectId)
      .is("deleted_at", null);
    for (const w of workPreps || []) add(w.name, "24", scopeOf(w.stage_id, w.area_survey_id));

    const lines = appendices.map(
      (a) => `• ${a.name} (Dok ${a.doc})${a.scope ? ` – ${a.scope}` : ""}`
    );
    return {
      text: lines.join("\n").trim(),
      count: appendices.length,
      sourceUpdatedAt: latest([
        ...((riskAssessments as any[]) || []),
        ...((skrAssessments as any[]) || []),
        ...((surveys as any[]) || []),
        ...((safetyDocs as any[]) || []),
        ...((workPreps as any[]) || []),
      ]),
      structuredData: { appendices },
    };
  }

  return empty;
}

/** Bakåtkompatibel variant som bara returnerar text. */
export async function fetchAmpSource(source: AmpSourceKind, projectId: string): Promise<string> {
  const result = await fetchAmpSourceDetailed(source, projectId);
  return result.text;
}
